import { supabase } from './supabase';
import CONFIG from './config';
import { classifyError, setAnalyticsUser, trackEvent } from './analytics';
import { browserSignupAttribution, loadSignupAttribution } from './signupAttribution';

const TRANSFER_KEY = 'instanttranscriber_guest_handover';
let pendingSession;
let pendingClaim;

export async function guestRequest(path, body, session) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), body === undefined ? 10000 : 30000);
  try {
    const response = await fetch(`${CONFIG.API_BASE}/guest/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: 'no-store', signal: controller.signal,
    });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.error || data.message || 'Please try again.'), { code: data.code, status: response.status });
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw Object.assign(new Error('The connection took too long. Please try again.'), { code: 'request_timeout' });
    throw error;
  } finally { clearTimeout(timeout); }
}

async function guestSessionEnded(error, session) {
  if (error.code === 'guest_session_expired') return true;
  if (![401, 403].includes(error.status)) return false;
  // Cleanup may already have removed the Auth user. Distinguish this from a
  // disabled rollout or network outage before discarding browser credentials.
  const checked = await supabase.auth.getUser(session.access_token);
  return ['user_not_found', 'session_not_found', 'refresh_token_not_found'].includes(checked.error?.code);
}

export function ensureGuestSession(verify) {
  if (pendingSession) return pendingSession;
  pendingSession = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session && !session.user.is_anonymous) return session;
    if (session) {
      try {
        await guestRequest('activity', {}, session);
        return session;
      } catch (error) {
        if (!await guestSessionEnded(error, session)) throw error;
        const latest = (await supabase.auth.getSession()).data.session;
        if (latest && (latest.user.id !== session.user.id || !latest.user.is_anonymous)) return latest;
        if (latest) await supabase.auth.signOut({ scope: 'local' });
      }
    }
    let stage = 'config';
    try {
      trackEvent('guest_session_start');
      const config = await guestRequest('config');
      if (!config.enabled) throw new Error('Guest transcription is not available yet. Please sign in to continue.');
      stage = 'verification';
      trackEvent('guest_verification_start');
      const captchaToken = await verify(config.turnstile_site_key);
      stage = 'ticket';
      const { ticket } = await guestRequest('signup-ticket', { captcha_token: captchaToken });
      trackEvent('guest_verification_complete');
      stage = 'signup';
      const { data, error } = await supabase.auth.signInAnonymously({ options: { data: {
        guest_ticket: ticket, signup_attribution: browserSignupAttribution(),
      } } });
      if (error) throw error;
      if (!data.session) throw new Error('Could not start your guest session. Please try again.');
      loadSignupAttribution(data.session.user).catch(error => console.warn('Guest attribution warning:', error.message));
      setAnalyticsUser(data.session.user);
      trackEvent('guest_session_created', { account_type: 'guest' });
      stage = 'activity';
      await guestRequest('activity', {}, data.session);
      return data.session;
    } catch (error) {
      trackEvent('guest_session_error', { guest_stage: stage, error_type: classifyError(error) });
      throw error;
    }
  })().finally(() => { pendingSession = null; });
  return pendingSession;
}

// The bearer proof never appears in URLs, analytics or persistent browser storage.
export async function beginGuestUpgrade() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.is_anonymous) return false;
  let grant;
  try { grant = await guestRequest('transfer-ticket', {}, session); }
  catch (error) {
    if (!await guestSessionEnded(error, session)) throw error;
    sessionStorage.removeItem(TRANSFER_KEY);
    return false;
  }
  sessionStorage.setItem(TRANSFER_KEY, JSON.stringify({ ticket: grant.ticket, expires: Date.now() + grant.expires_in * 1000 }));
  return true;
}

export function completeGuestUpgrade(session) {
  if (!session || session.user.is_anonymous) return Promise.resolve();
  if (pendingClaim) return pendingClaim;
  pendingClaim = (async () => {
    const saved = sessionStorage.getItem(TRANSFER_KEY);
    if (!saved) return;
    const grant = JSON.parse(saved);
    if (grant.expires <= Date.now()) throw Object.assign(new Error('Your guest handover has expired. Contact support if you need help recovering your transcripts.'), { code: 'guest_transfer_expired' });
    await guestRequest('claim', { ticket: grant.ticket }, session);
    sessionStorage.removeItem(TRANSFER_KEY);
    window.dispatchEvent(new Event('instanttranscriber:guest-saved'));
    return { saved: true };
  })().finally(() => { pendingClaim = null; });
  return pendingClaim;
}
